import { useCallback, useEffect, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";
import { readApiCode, readApiStatus } from "@/shared/utils/apiError";

import { CardsService } from "../services/cardsService";
import type { CardDepositAddress } from "../types/cards-api.types";
import { readDepositAddresses } from "../utils/cardDeposit";

/** The backend's 403 code while the deposit QR is not open for the card's organization (rollout). */
export const CARD_DEPOSITS_NOT_ENABLED_CODE = "card_deposits_not_enabled";

export interface UseCardDepositAddressesOptions {
  /** Fetch only while true. */
  enabled: boolean;
}

export interface UseCardDepositAddressesState {
  addresses: CardDepositAddress[];
  isLoading: boolean;
  error: string | null;
  /** The feature is not open for the card's organization: render no deposit UI at all. */
  notEnabled: boolean;
  /** The backend refused because the card is not ACTIVE (409). */
  notActive: boolean;
  /** A read for this card has finished at least once (a retry keeps it true while it runs). */
  hasSettled: boolean;
  refresh: () => Promise<void>;
}

/** How a settled read ended, apart from the addresses themselves. */
export type DepositAddressesFailure = "not_enabled" | "not_active" | "error";

/**
 * Classifies a failed read. The rollout 403 and the not-ACTIVE 409 are answers, not errors: the first hides
 * the panel, the second shows the "not active" note. Anything else is a retryable error.
 */
export function classifyDepositAddressesError(error: unknown): DepositAddressesFailure {
  if (readApiStatus(error) === 403 && readApiCode(error) === CARD_DEPOSITS_NOT_ENABLED_CODE) {
    return "not_enabled";
  }
  if (readApiStatus(error) === 409) return "not_active";
  return "error";
}

/** The last settled read, stamped with the card it belongs to. */
interface DepositAddressesOutcome {
  cardId: string;
  addresses: CardDepositAddress[];
  error: string | null;
  failure: DepositAddressesFailure | null;
}

/**
 * The card's on-chain deposit addresses. A sequence ref drops out-of-order responses, and the outcome is
 * keyed by card: navigating to another card can never show the previous card's address or error, not even for
 * the render before the new request starts — for a destination of real money, a stale value is worse than
 * none.
 */
export function useCardDepositAddresses(
  cardId: string | undefined,
  { enabled }: UseCardDepositAddressesOptions,
): UseCardDepositAddressesState {
  const [outcome, setOutcome] = useState<DepositAddressesOutcome | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    if (!enabled || !cardId) {
      setIsFetching(false);
      return;
    }
    setIsFetching(true);
    try {
      const data = await CardsService.getCardDepositAddresses(cardId);
      if (seq !== seqRef.current) return;
      setOutcome({ cardId, addresses: readDepositAddresses(data), error: null, failure: null });
    } catch (err: unknown) {
      if (seq !== seqRef.current) return;
      const failure = classifyDepositAddressesError(err);
      const message =
        failure === "error"
          ? HttpApiService.getErrorMessage(err) || "Could not load the deposit addresses"
          : null;
      setOutcome({ cardId, addresses: [], error: message, failure });
    } finally {
      if (seq === seqRef.current) setIsFetching(false);
    }
  }, [cardId, enabled]);

  useEffect(() => {
    void load();
  }, [load]);

  const active = enabled && !!cardId;
  const current = active && outcome?.cardId === cardId ? outcome : null;
  // Nothing settled for this card yet counts as loading, so the first render is a skeleton, not a blank.
  const isLoading = active && (isFetching || current === null);

  return {
    addresses: current?.addresses ?? [],
    isLoading,
    error: isLoading ? null : (current?.error ?? null),
    notEnabled: !isLoading && current?.failure === "not_enabled",
    notActive: !isLoading && current?.failure === "not_active",
    hasSettled: current !== null,
    refresh: load,
  };
}
